import type { TileRow } from "@kundeninfo/api-client";
import { fileSizeText, formatDatum } from "../../format.js";
import { DOCUMENT_KIND_TEXT, FACT_TEXT, MARKER_TEXT, ROW_TEXT } from "./texts.js";
import type { LevelWord } from "./texts.js";
import type { TileMapping } from "./tileCatalog.js";
import { dayText, flagged, levelOf, markerText, nextDateFact, rowView } from "./tileRows.js";

/*
 * Die übrigen Serverkacheln: Wichtiges, Tickets, Notizen, Dateien und Konzern. Reihenfolge und Zustand der Zeilen
 * kommen fertig vom Server; hier steht nur, wie eine Zeile und die Kennzahlen aussehen.
 */

const NO_FACTS: TileMapping["facts"] = () => [];

/** Kritische Hinweise sind Fehler, wichtige Warnungen; alle anderen tragen keinen Zustand. */
const NOTE_STATE: Record<NonNullable<TileRow["level"]>, LevelWord> = {
  ERROR: { statusArt: "fehler", status: ROW_TEXT.critical },
  WARN: { statusArt: "warnung", status: ROW_TEXT.important },
};

/** Der volle Tag („26.10.2026“) für Notizen und Dateien, deren Alter zählt; ohne Datum `null`. */
const fullDay = (r: TileRow): string | null => (r.date === null ? null : formatDatum(r.date));

/** Der erste verknüpfte Vorgang einer Datei („Angebot AN-…“): Art aus `status`, Nummer aus `reference`; sonst leer. */
const firstLink = (r: TileRow): string =>
  (r.reference !== null && r.status !== null && Object.hasOwn(DOCUMENT_KIND_TEXT, r.status)
    ? ROW_TEXT.firstLink(r.status as keyof typeof DOCUMENT_KIND_TEXT, r.reference) : "");

export const ACTIVITY_TILE_MAPPINGS: Record<"importantNotes" | "tickets" | "notes" | "files" | "group", TileMapping> = {
  importantNotes: {
    facts: NO_FACTS,
    row: (r, ctx) => {
      const day = dayText(r, ctx);
      // Eine Laufzeitwarnung nennt Auftrag und Warndatum; ein Hinweis seinen Text und, wenn er endet, „bis“.
      return r.marker === "ORDER_ENDING"
        ? rowView(r, ROW_TEXT.orderEnds(r.text), day, [], { statusArt: "warnung", status: MARKER_TEXT.ORDER_ENDING })
        : rowView(r, r.text, day === null ? null : ROW_TEXT.until(day), [], r.level === null ? {} : NOTE_STATE[r.level]);
    },
  },
  tickets: {
    facts: (f, ctx) => [flagged(FACT_TEXT.overdue, f.overdue, "fehler"),
      nextDateFact(FACT_TEXT.nextResubmission, "resubmission", f, ctx)],
    row: (r, ctx) => rowView(r, r.text, dayText(r, ctx), [r.detail ?? ROW_TEXT.unassigned, r.status ?? ""],
      levelOf("resubmission", r, ctx)),
  },
  notes: {
    facts: NO_FACTS,
    row: (r) => rowView(r, r.text, fullDay(r), [r.detail ?? ""], {}),
  },
  files: {
    facts: NO_FACTS,
    // Die Nummer des Vorgangs steht mit ihrer Art in der zweiten Zeile, nicht als Kennung der Datei.
    row: (r) => rowView({ ...r, reference: null }, r.text, fullDay(r),
      [firstLink(r), r.detail ?? ROW_TEXT.unknown, fileSizeText(r.count ?? 0)], {}),
  },
  group: {
    facts: NO_FACTS,
    row: (r) => rowView(r, r.text, null, [markerText(r)], {}),
  },
};
